/**
 * `getSlack(task1, task2)` — path-enumeration slack between two tasks.
 *
 * Implements D-14 per `04-` §17.2.
 *
 * Algorithm:
 *   1. If either task is in `omitSet` (cycle member), return 0.
 *   2. If t1 === t2: return that task's totalSlack.
 *   3. DFS from `t1` looking for `t2`, summing per-edge slacks; record min.
 *   4. DFS from `t2` looking for `t1`, same.
 *   5. Return `min(forward, reverse)`. If no path found in either direction:
 *      `Infinity` (adapter converts to v1's sentinel at the public boundary).
 *
 * Per-edge slack = `target.earliestStart − applyEdgeForward(source.earliestEnd, edge)`,
 * converted to minutes via the source's calendar (matches v1 semantics —
 * slack is reported in the source task's working time).
 *
 * Containment edges (summary → descendant) contribute zero slack in BOTH
 * traversal directions per D-14's "Parent-child pair handled via containment
 * edges" rule.
 *
 * Path-depth cap (`MAX_DEPTH = 64`) prevents O(2^V) DFS blowup on highly
 * connected graphs. Past the cap the branch is pruned — the returned min is
 * still an upper bound on actual slack (correct for typical CPM reporting).
 */

import type { NormalizedTaskID } from "../types";
import type { AnalysisGraph, AnalysisEdge, SlackEntry } from "../graph/types";
import { applyEdgeForward } from "./slack";

const MAX_DEPTH = 64;

/** Per-edge slack in source's working minutes. */
function edgeSlackMinutes(
  edge: AnalysisEdge,
  slack: ReadonlyMap<NormalizedTaskID, SlackEntry>,
  graph: AnalysisGraph
): number {
  const sourceEntry = slack.get(edge.source);
  const targetEntry = slack.get(edge.target);
  if (!sourceEntry || !targetEntry) return 0;
  const requiredTargetStart = applyEdgeForward(sourceEntry.earliestEndTs, edge);
  const gapMs = targetEntry.earliestStartTs - requiredTargetStart;
  if (gapMs <= 0) return 0;
  const sourceNode = graph.nodes.get(edge.source);
  if (!sourceNode) return 0;
  return sourceNode.calendar.calculateDuration(
    new Date(sourceEntry.earliestEndTs),
    new Date(sourceEntry.earliestEndTs + gapMs),
    "minute"
  );
}

function enumerateAndMin(
  graph: AnalysisGraph,
  slack: ReadonlyMap<NormalizedTaskID, SlackEntry>,
  from: NormalizedTaskID,
  to: NormalizedTaskID,
  omitSet: ReadonlySet<NormalizedTaskID>,
  omitEdgePairs: ReadonlySet<string>
): number {
  let minSlack = Number.POSITIVE_INFINITY;
  const visited = new Set<NormalizedTaskID>();

  // Precompute containment neighbors for both directions to avoid scanning
  // the full containment list at every DFS node.
  const containmentForward = new Map<NormalizedTaskID, NormalizedTaskID[]>();   // summary → descendants
  const containmentReverse = new Map<NormalizedTaskID, NormalizedTaskID[]>();   // descendant → summaries
  for (const c of graph.containment) {
    if (omitSet.has(c.summary) || omitSet.has(c.descendant)) continue;
    let f = containmentForward.get(c.summary);
    if (!f) { f = []; containmentForward.set(c.summary, f); }
    f.push(c.descendant);
    let r = containmentReverse.get(c.descendant);
    if (!r) { r = []; containmentReverse.set(c.descendant, r); }
    r.push(c.summary);
  }

  const dfs = (node: NormalizedTaskID, accum: number, depth: number): void => {
    if (node === to) {
      if (accum < minSlack) minSlack = accum;
      return;
    }
    if (depth >= MAX_DEPTH) return;
    if (visited.has(node)) return;
    visited.add(node);

    // Forward over successor (scheduling) edges.
    const succs = graph.successors.get(node) ?? [];
    for (const e of succs) {
      if (omitSet.has(e.target)) continue;
      if (omitEdgePairs.has(e.source + "|" + e.target)) continue;
      const eSlack = edgeSlackMinutes(e, slack, graph);
      dfs(e.target, accum + eSlack, depth + 1);
    }

    // Containment in both directions, zero slack.
    const cf = containmentForward.get(node);
    if (cf) {
      for (const d of cf) dfs(d, accum, depth + 1);
    }
    const cr = containmentReverse.get(node);
    if (cr) {
      for (const s of cr) dfs(s, accum, depth + 1);
    }

    visited.delete(node);
  };

  dfs(from, 0, 0);
  return minSlack;
}

/**
 * Compute the slack between two tasks per D-14.
 *
 * @returns Slack in minutes, or `Infinity` if no path exists between the
 *          two tasks. Adapter converts Infinity to v1's sentinel at the
 *          public-method boundary.
 */
export function getSlackBetween(
  graph: AnalysisGraph,
  slack: ReadonlyMap<NormalizedTaskID, SlackEntry>,
  t1: NormalizedTaskID,
  t2: NormalizedTaskID,
  omitSet: ReadonlySet<NormalizedTaskID>,
  omitEdgePairs: ReadonlySet<string> = new Set()
): number {
  if (omitSet.has(t1) || omitSet.has(t2)) return 0;
  if (t1 === t2) {
    const entry = slack.get(t1);
    return entry ? entry.totalSlack : 0;
  }

  const forwardMin = enumerateAndMin(graph, slack, t1, t2, omitSet, omitEdgePairs);
  const reverseMin = enumerateAndMin(graph, slack, t2, t1, omitSet, omitEdgePairs);

  const min = Math.min(forwardMin, reverseMin);
  return Number.isFinite(min) ? min : Infinity;
}
